import type { ArtifactScope, ArtifactStatus, ArtifactType } from './api';

const ARTIFACT_TYPES: readonly ArtifactType[] = [
  'issue',
  'spec',
  'adr',
  'implementation_plan',
  'note',
];
const ARTIFACT_SCOPES: readonly ArtifactScope[] = ['codebase', 'workstream'];
const ARTIFACT_STATUSES: readonly ArtifactStatus[] = [
  'draft',
  'review',
  'approved',
  'superseded',
  'archived',
];

const DEFAULT_PAGE_SIZE = 25;
// One below the server's hard cap (200, see `listArtifactsQuerySchema`) so
// the list view's "fetch pageSize + 1 to detect hasNext" trick stays
// inside the server's allowed range even at the maximum page size.
const MAX_PAGE_SIZE = 199;

export interface ArtifactListFiltersUi {
  type?: ArtifactType;
  scope?: ArtifactScope;
  status?: ArtifactStatus;
  ownerId?: string;
  search?: string;
  includeArchived: boolean;
  page: number;
  pageSize: number;
}

function isType(v: string): v is ArtifactType {
  return (ARTIFACT_TYPES as readonly string[]).includes(v);
}
function isScope(v: string): v is ArtifactScope {
  return (ARTIFACT_SCOPES as readonly string[]).includes(v);
}
function isStatus(v: string): v is ArtifactStatus {
  return (ARTIFACT_STATUSES as readonly string[]).includes(v);
}

export function encodeArtifactFilters(filters: Partial<ArtifactListFiltersUi>): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.status) params.set('status', filters.status);
  if (filters.ownerId) params.set('ownerId', filters.ownerId);
  if (filters.search) params.set('search', filters.search);
  if (filters.includeArchived) params.set('includeArchived', 'true');
  if (filters.page && filters.page > 0) params.set('page', String(filters.page));
  if (filters.pageSize && filters.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(filters.pageSize));
  }
  return params;
}

export function decodeArtifactFilters(params: URLSearchParams): ArtifactListFiltersUi {
  const rawType = params.get('type');
  const rawScope = params.get('scope');
  const rawStatus = params.get('status');
  const rawOwner = params.get('ownerId');
  const rawSearch = params.get('search');
  const rawArchived = params.get('includeArchived');
  const rawPage = Number(params.get('page'));
  const rawPageSize = Number(params.get('pageSize'));

  const page = Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 0;
  const pageSize = ((): number => {
    if (!Number.isFinite(rawPageSize) || rawPageSize <= 0) return DEFAULT_PAGE_SIZE;
    return Math.min(MAX_PAGE_SIZE, Math.floor(rawPageSize));
  })();

  const status = rawStatus && isStatus(rawStatus) ? rawStatus : undefined;
  // Hand-crafted-URL normalization: status=archived implies includeArchived,
  // since the two facts cannot coherently disagree at query time. The filters
  // bar enforces the same invariant when the user changes the dropdown or
  // toggle directly; this branch handles direct/shareable URLs.
  const includeArchived = status === 'archived' ? true : rawArchived === 'true';

  return {
    type: rawType && isType(rawType) ? rawType : undefined,
    scope: rawScope && isScope(rawScope) ? rawScope : undefined,
    status,
    ownerId: rawOwner ?? undefined,
    search: rawSearch ?? undefined,
    includeArchived,
    page,
    pageSize,
  };
}
